'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_SORT,
  NO_RECORDS_FOUND,
  SORT_OPTIONS,
  sortOptionFor,
  emptyFilters,
  listingQuery,
  removeChip,
  sortLabel,
  stageIndexAt,
  FILTER_STAGES,
  type AttractionFilters as Filters,
  type AttractionSummary,
  type FilterChip,
  type ListingFacets,
  type SortValue,
} from '../config';
import { LISTING_PAGE_SIZE, searchAttractions } from '../_data/catalogue';
import { AttractionCard } from './AttractionCard';
import { AttractionsFilters } from './AttractionsFilters';
import { FilterChips } from './FilterChips';
import { CardSkeleton, ResultsSkeleton } from './Skeletons';
import { CityLoadingNotice } from './CityLoadingNotice';
import type { CityStatus } from '../_data/attractions.api';
import { ChevronUpIcon, GridIcon, ListIcon } from './Icons';

interface AttractionsListProps {
  /** SSR-fetched first page — rendered on the server, then owned by this component. */
  initialData: AttractionSummary[];
  /** Count of everything matching the current filters, not the length of this page. */
  total: number;
  totalPages: number;
  cityName: string;
  /**
   * The destination and free-text query, carried through every refetch.
   *
   * `cityId` is the canonical Bridgify id and the one that actually filters —
   * every page, every filter change and every facet rebuild sends it, which is
   * what keeps the grid and the sidebar describing the SAME city. `city` is the
   * display name, and the fallback filter for the destinations Bridgify ships
   * without an id.
   */
  cityId?: number;
  city: string;
  citySlug?: string;
  query: string;
  /** Panel contents for the SSR'd result set. */
  initialFacets: ListingFacets;
  /** The searched city's freshness; `loading` = partial results, see `CityLoadingNotice`. */
  cityStatus?: CityStatus | null;
  /** Filter + sort + view state as the URL expressed it. */
  initialFilters: Filters;
  initialSort: SortValue;
  initialView: 'grid' | 'list';
}

/**
 * Listing body: chips + toolbar (count / sort / view toggle) + sidebar + results.
 *
 * Filtering, sorting and counting all run in the SERVICE. The catalogue is
 * ~10,000 products and a destination can hold far more than one page, so a
 * client-side filter over the first page would have produced counts that
 * described only that page and a "sort by price" that could not see the cheapest
 * product in the city.
 *
 * State lives in the URL, written with `history.replaceState` rather than a
 * router navigation — the same pattern the hotels listing uses. That keeps
 * back-navigation and a shared link exact (the server component re-reads the
 * params on every render) without a full RSC round-trip on every checkbox
 * click, which would fetch the page twice.
 *
 * Sort and filters are INDEPENDENT state. Neither resets the other
 * (BCP-2063 §4); only Reset clears both.
 */
export function AttractionsList({
  initialData,
  total,
  totalPages,
  cityName,
  cityId,
  city,
  citySlug,
  query,
  initialFacets,
  cityStatus,
  initialFilters,
  initialSort,
  initialView,
}: AttractionsListProps) {
  const cityLoading = cityStatus?.state === 'loading' && cityId !== undefined;
  const cityFailed = cityStatus?.state === 'error';
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [sort, setSort] = useState<SortValue>(initialSort);
  const [view, setView] = useState<'grid' | 'list'>(initialView);

  const [items, setItems] = useState<AttractionSummary[]>(initialData);
  const [facets, setFacets] = useState<ListingFacets>(initialFacets);
  const [count, setCount] = useState(total);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(totalPages);
  const [loading, setLoading] = useState(false);

  // Refs the IntersectionObserver reads — the observer is registered once and
  // must not close over a stale page number.
  const pageRef = useRef(page);
  const pagesRef = useRef(pages);
  const loadingRef = useRef(loading);
  pageRef.current = page;
  pagesRef.current = pages;
  loadingRef.current = loading;

  /** Guards against an earlier in-flight request overwriting a later one. */
  const requestRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  /*
   * Refetch progress, matching the hotels listing's filter bar.
   *
   * `refetching` is NOT the same as `loading`: loading is also true while
   * infinite scroll appends a page, and replacing the grid with placeholders
   * then would throw away results the visitor is already reading. This is set
   * only for a from-scratch fetch — a filter, a sort or a new search.
   */
  const [refetching, setRefetching] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [progress, setProgress] = useState(2);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopProgress = useCallback((complete: boolean) => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    // Snap to full before unmounting the bar, so it never disappears mid-track.
    if (complete) setProgress(100);
    setRefetching(false);
  }, []);

  /**
   * Advance the cosmetic stage timer.
   *
   * Capped at 92% while the request is in flight: this is one REST call with no
   * real progress to report, and a bar that reached 100% before the results
   * arrived would read as "finished" over a stale grid. The response is what
   * completes it.
   */
  const startProgress = useCallback(() => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setRefetching(true);
    setStageIdx(0);
    setProgress(2);

    const total = FILTER_STAGES.reduce((a, b) => a + b.duration, 0);
    let elapsed = 0;
    progressTimerRef.current = setInterval(() => {
      elapsed += 150;
      setProgress(Math.min((elapsed / total) * 92, 92));
      setStageIdx(stageIndexAt(FILTER_STAGES, elapsed));
    }, 150);
  }, []);

  // A timer must not outlive the component — an unmount mid-refetch would
  // otherwise keep firing setState on a dead tree.
  useEffect(() => () => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
  }, []);

  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  // Close the sort menu on an outside click or Escape — a custom menu has to
  // do for itself what a native <select> got from the platform.
  useEffect(() => {
    if (!sortOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSortOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [sortOpen]);

  /**
   * Nothing to present.
   *
   * Everything in the toolbar and the sidebar exists to CHANGE a result set:
   * sorting none of them, switching the layout of none of them, or ticking a
   * filter that can only take 0 to 0, are all controls that look functional and
   * cannot do anything. `loading` is excluded so the controls do not flicker
   * out and back during a refetch that is about to return rows.
   */
  const noResults = count === 0 && !loading;

  /**
   * When the grid is replaced by placeholders.
   *
   * Mirrors the hotels listing's own condition: a from-scratch refetch, OR a
   * load that has nothing on screen to preserve yet. The second clause matters
   * only on a cold list — normally SSR has already delivered page one.
   */
  const showSkeleton = refetching || (items.length === 0 && count === 0 && loading);

  // A menu left open when the last result disappears would hang over an empty
  // page with nothing to act on.
  useEffect(() => {
    if (noResults) setSortOpen(false);
  }, [noResults]);

  const fetchPage = useCallback(
    async (nextPage: number, nextFilters: Filters, nextSort: SortValue, append: boolean) => {
      const requestId = ++requestRef.current;
      setLoading(true);
      // Only a from-scratch fetch gets the progress bar and placeholders; an
      // append must leave the results already on screen alone.
      if (!append) startProgress();
      try {
        const res = await searchAttractions({
          // Same as the SSR fetch: the id filters, the name labels. The
          // catalogue layer sends only the id to the service when it has one.
          cityId,
          city,
          query,
          filters: nextFilters,
          sort: nextSort,
          page: nextPage,
          limit: LISTING_PAGE_SIZE,
        });
        // A slower earlier request must not clobber a newer result — ticking
        // three checkboxes quickly fires three overlapping fetches.
        if (requestId !== requestRef.current) return;

        setItems((prev) => (append ? [...prev, ...res.data] : res.data));
        setCount(res.total);
        setPage(res.page);
        setPages(res.totalPages);
        // Facets ride page 1 only; an appended page leaves the panel alone.
        if (!append) setFacets(res.facets);
      } finally {
        // Guarded by the same race check as the result: a superseded request
        // must not tear down the progress UI that the newer one now owns.
        if (requestId === requestRef.current) {
          setLoading(false);
          if (!append) stopProgress(true);
        }
      }
    },
    [cityId, city, query, startProgress, stopProgress],
  );

  /**
   * Commit new state: record it in the URL, then refetch from page 1.
   *
   * Results update IMMEDIATELY on every selection and deselection — there is no
   * Apply step on the inline controls (BCP-2060 §2, BCP-2063 §7). The More
   * popup is the one staged surface, and it reaches here only through its Apply
   * button.
   */
  const commit = useCallback(
    (next: { filters?: Filters; sort?: SortValue; view?: 'grid' | 'list' }) => {
      const nextFilters = next.filters ?? filters;
      const nextSort = next.sort ?? sort;
      const nextView = next.view ?? view;

      if (next.filters) setFilters(next.filters);
      if (next.sort) setSort(next.sort);
      if (next.view) setView(next.view);

      const qs = listingQuery({
        // Only the readable value reaches the URL; `cityId` stays in component
        // state and in the API calls.
        citySlug: citySlug || city,
        query,
        filters: nextFilters,
        sort: nextSort,
        view: nextView,
      });
      window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);

      // A view change is presentation only — it must not alter the result set,
      // the filters or the sort (BCP-2058 §4), so it never refetches.
      if (next.filters || next.sort) {
        void fetchPage(1, nextFilters, nextSort, false);
      }
    },
    [citySlug, city, query, filters, sort, view, fetchPage],
  );

  /** Reset — clears every filter AND restores the default recommendation order. */
  const reset = useCallback(() => {
    commit({ filters: emptyFilters(), sort: DEFAULT_SORT });
  }, [commit]);

  /** Chip Clearing — removes only that selection; the sort and other filters stand. */
  const clearChip = useCallback(
    (chip: FilterChip) => commit({ filters: removeChip(filters, chip) }),
    [commit, filters],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          pageRef.current < pagesRef.current &&
          !loadingRef.current
        ) {
          void fetchPage(pageRef.current + 1, filters, sort, true);
        }
      },
      { threshold: 0.1 },
    );
    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [fetchPage, filters, sort]);

  return (
    <div className={`atr-listing${noResults ? ' atr-listing--empty' : ''}`}>
      {/*
        The sidebar is REMOVED, not disabled, when there is nothing to filter.
        Its counts describe the current result set, so at zero every option
        would read "(0)" — a panel full of dead ends. Unmounting also
        guarantees no stale option from the previous city survives, and
        collapses the grid to a single column via `--empty`.
      */}
      {!noResults && (
        <AttractionsFilters
          value={filters}
          onChange={(next) => commit({ filters: next })}
          facets={facets}
        />
      )}

      <div>
        <div className="atr-toolbar">
          {/* Hidden at zero: "{city} Attractions & Experiences : 0 found" is a summary OF a
              result set, and restating that there is nothing directly above a
              message saying so reads as a double negative. The controls on the
              right stay put, greyed. */}
          {!noResults && (
            <h2 className="atr-toolbar__count">
              {cityName} Attractions &amp; Experiences : <span>{count} found</span>
            </h2>
          )}

          <div className="atr-toolbar__controls">
            {/*
              Sort control, styled after the hotels listing's dropdown: a pill
              trigger reading "Sort by: {active}" with a chevron, over a rounded
              menu whose selected row is a filled brand pill.

              A custom menu rather than a native <select> purely for appearance —
              a native select renders the OS list (white rows, blue highlight)
              and cannot be styled to match. The values, the state and the
              `commit()` call are exactly as they were.

              The trigger still reads "Sort by : {label}" as the sort tickets
              require; it is only split across two spans so the prefix can be
              muted the way the hotels pill mutes it. `sortLabel` drives the
              accessible name so the spec'd string is what assistive tech hears.
            */}
            <div className="atr-sort" ref={sortRef}>
              <button
                type="button"
                className="atr-sort__trigger"
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                aria-label={sortLabel(sort)}
                // Kept visible but inert: the toolbar would otherwise reflow as
                // results come and go. `disabled` also blocks the keyboard,
                // which hiding the menu alone would not.
                disabled={noResults}
                onClick={() => setSortOpen((o) => !o)}
              >
                <span className="atr-sort__prefix">Sorted by:</span>
                <span className="atr-sort__value">{sortOptionFor(sort).label}</span>
                <ChevronUpIcon />
              </button>

              {sortOpen && !noResults && (
                <div className="atr-sort__menu" role="listbox" aria-label="Sort by">
                  {SORT_OPTIONS.map((o) => (
                    <button
                      key={o.param}
                      type="button"
                      role="option"
                      aria-selected={sort === o.param}
                      className={`atr-sort__option${sort === o.param ? ' is-active' : ''}`}
                      onClick={() => {
                        setSortOpen(false);
                        commit({ sort: o.param });
                      }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="atr-viewtoggle" role="group" aria-label="Result layout">
              <button
                type="button"
                aria-pressed={view === 'list'}
                aria-label="List view"
                disabled={noResults}
                onClick={() => commit({ view: 'list' })}
              >
                <ListIcon />
              </button>
              <button
                type="button"
                aria-pressed={view === 'grid'}
                aria-label="Grid view"
                disabled={noResults}
                onClick={() => commit({ view: 'grid' })}
              >
                <GridIcon />
              </button>
            </div>
          </div>
        </div>

        {/*
          Refetch progress, matching the hotels listing's filter bar.

          ABOVE the filter chips, as hotels orders it: the bar reports on the
          change the visitor just made, so it belongs between the control they
          used and the results it is reloading — not below the chips, where it
          reads as a footnote to them.
        */}
        {refetching && (
          <div className="atr-progress" role="status" aria-live="polite">
            <p className="atr-progress__label">
              <i className="fas fa-spinner fa-spin atr-progress__spinner" aria-hidden="true" />
              {FILTER_STAGES[stageIdx]?.label}
            </p>
            <div className="atr-progress__track">
              <div className="atr-progress__bar" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Chips are the sidebar's selections restated — with the sidebar gone
            they would be the only way to alter a filter, on a set that has
            nothing to alter. */}
        {!noResults && (
          <FilterChips
            filters={filters}
            facets={facets}
            onRemove={clearChip}
            onReset={reset}
          />
        )}

        {cityLoading && (
          <CityLoadingNotice cityId={cityId!} cityName={city} shown={count} expectedTotal={cityStatus?.expectedTotal ?? null} />
        )}

        {showSkeleton ? (
          // Placeholders rather than the previous results: a filter change
          // makes what is on screen wrong, and leaving it there while the new
          // set loads reads as "the filter did nothing".
          <ResultsSkeleton view={view} count={6} />
        ) : items.length === 0 && !loading ? (
          cityLoading ? null : (
            <p className="atr-empty" role="status">
              {cityFailed
                ? `We couldn't load attractions${city ? ` in ${city}` : ''} right now. Please try again in a moment.`
                : NO_RECORDS_FOUND}
            </p>
          )
        ) : (
          <div className={view === 'grid' ? 'atr-grid' : 'atr-list'}>
            {items.map((a) => (
              <AttractionCard key={a.id} attraction={a} view={view} />
            ))}
            {/*
              Load-more placeholders live INSIDE the same grid, as hotels does
              them, so they inherit its gap and — in grid view — fill the blank
              slots of the last partial row instead of starting a detached grid
              below it.
            */}
            {loading &&
              !refetching &&
              [0, 1, 2].map((i) => <CardSkeleton key={`more-${i}`} view={view} />)}
          </div>
        )}

        {/* Infinite-scroll tripwire. */}
        <div ref={sentinelRef} style={{ height: 32 }} />
      </div>
    </div>
  );
}
